import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import PieChartIcon from "@mui/icons-material/PieChart";
import SpeedIcon from "@mui/icons-material/Speed";
import SquareIcon from "@mui/icons-material/Square";
import TableChartIcon from "@mui/icons-material/TableChart";

import { withTranslation } from "react-i18next";
import { debounce } from "lodash";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import fpDashboardService from "@plannel/services/fp/fp-dashboard-service";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import { FullScreenButton } from "@plannel/components/ActionIconButton";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

// old DeliveryStatusSummary.jsx theme 상수 — 납기준수(primary)/지연(warning)/실패(danger).
// 5008(SimulationKPIDashboard)이 같은 old 컴포넌트를 이관하며 쓴 것과 동일한 값이다.
const PIE_COLOR_OK = "#3B7DDD";
const PIE_COLOR_LATE = "#fcb92c";
const PIE_COLOR_SHORT = "#dc3545";
// old DeliveryDelayStatus.jsx / DeliveryShortStatus.jsx의 backgroundColor 배열 10색 그대로.
// 슬라이스 순서 = 서버 LinkedHashMap 순서(건수 내림차순)라 팔레트 인덱스가 곧 순위다.
const DELAY_COLORS = ["#F35B04", "#F18701", "#FD9E02", "#F7B801", "#7678ED", "#3D348B", "#023047", "#126782", "#219EBC", "#8ECAE6"];
const SHORT_COLORS = ["#7A0103", "#8E0103", "#A50104", "#B81702", "#EC3F13", "#FA5E1F", "#FF7E33", "#FF931F", "#FFAD33", "#FFB950"];

// old options.cutoutPercentage = 70은 Chart.js **v2 키**라 old가 쓰는 v3.9.1에서는 무시되고
// doughnut 기본값(50%)으로 렌더됐다 → 실제로 보이던 화면과 같게 50%로 이관한다.
const DONUT_INNER_RADIUS = "50%";

// 전체화면(카드 1개만 확대) 대상 식별자 — 대시보드 카테고리 공통 규칙(§C-4b)대로 카드 전부에 단다.
const CARD = {
  ORDER_CNT: "orderCount",
  ONTIME_RATE: "onTimeDeliveryRate",
  DELIVERY: "deliveryStatus",
  DELAY: "deliveryDelayStatus",
  SHORT: "deliveryShortStatus",
  PROBLEM: "problemGrid",
};

// old wingui-core getFormattedNumber 대응 — 천단위 + 소수 최대 2자리(FP 대시보드 트랙 기준, §C-4c).
const formatNumber = (value) => {
  if (value == null || value === "") return value;
  const num = Number(value);
  return Number.isNaN(num) ? value : num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

// 도넛 1개분 행 변환 — 서버가 사유코드별 건수를 **건수 내림차순 LinkedHashMap**으로 내려주므로
// Object.entries의 삽입 순서를 그대로 쓴다(재정렬 금지 — 팔레트 인덱스가 곧 순위다).
// 실제 사유는 ProblemReasonType enum 8종이라 10색을 넘지 않지만, old가 11번째부터 색이
// undefined가 되던 것을 방어해 나머지 연산으로 순환시킨다.
const buildDonutRows = (statusMap, colors, t) =>
  Object.entries(statusMap ?? {}).map(([code, count], index) => ({
    code,
    name: t(code),
    value: Number(count ?? 0),
    color: colors[index % colors.length],
  }));

// old ProblemDetailGrid valueCallback — "코드 (명칭)" 결합(양쪽 trim, 빈값 방어).
const joinCodeName = (cd, nm) => {
  const code = String(cd ?? "").trim();
  const name = String(nm ?? "").trim();
  if (!code) return name;
  if (!name) return code;
  return `${code} (${name})`;
};

const PlanProblemDashboard = ({ t, viewName, title }) => {
  // VersionSearchGroup(controlled) — 캐스케이드 각 단계가 부분 객체로 통지(§C-1).
  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });

  // 응답 원본 3종: kpi = KpiResultDto(타일 2 + 파이), problemStatus = 사유코드별 건수 맵 2개(도넛),
  // problemRows = PlanProblemDto 배열(그리드).
  const [kpi, setKpi] = useState(null);
  const [delayStatus, setDelayStatus] = useState(null);
  const [shortStatus, setShortStatus] = useState(null);
  const [problemRows, setProblemRows] = useState([]);

  // gridRef는 로딩 오버레이용(debounce 클로저에서도 현재 인스턴스를 가리켜야 한다),
  // gridApi state는 컬럼 정의 적용용 — 전체화면 전환이 그리드를 재마운트하면 새 api로 effect가
  // 다시 돌아야 컬럼이 다시 걸린다(§C-4b).
  const gridRef = useRef();
  const [gridApi, setGridApi] = useState(null);

  // 전체화면 — 공용 FullScreenButton 계약대로 boolean + 대상 id 2개.
  const [fullScreen, setFullScreen] = useState(false);
  const [fullScreenCardId, setFullScreenCardId] = useState(null);

  // 전체화면 전환은 본문을 카드 하나로 "교체"하는 방식이라 그리드가 다른 부모 아래에서 다시
  // 그려진다 → React가 AgGridReact를 재마운트해 사용자가 바꾼 컬럼 순서/폭/정렬이 통째로
  // 사라진다(GridUtils.setColumnDefs의 상태 보존은 같은 인스턴스에서만 유효 — 새 인스턴스는
  // 캡처 시점에 컬럼이 0개다). 전환 직전에 저장해 뒀다가 새 인스턴스가 준비되면 되돌린다.
  const columnStateRef = useRef(null);
  const captureColumnState = () => {
    const api = gridRef.current?.api;
    if (api && !api.isDestroyed()) columnStateRef.current = api.getColumnState();
  };

  // 응답 순서 뒤바뀜 방지 토큰 — 병렬 API 3개가 한 조회 단위라 토큰은 1개(fp-frontend.md §0-8).
  const latestReqRef = useRef(0);
  // debounce 콜백(최초 렌더 클로저)에서 항상 최신 조회조건을 읽기 위한 ref(§C-1).
  const searchStateRef = useRef(versionState);
  searchStateRef.current = versionState;

  const defaultGridMemo = useMemo(() => {
    const settings = FpGridSetting({ title, viewName });
    return {
      ...settings,
      // 읽기 전용 대시보드(old setNoneEditableGrid 대응) — 저장/삭제/DataState 추적 없음.
      defaultColDef: { ...settings.defaultColDef, editable: false },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // old loadBoard()는 problemDelay만 비우고 dashCondition/problemDetailGridData는 안 비워서
  // 재조회 중 이전 버전의 타일/그리드가 그대로 남아 있었다 → 4개 상태를 모두 초기화한다.
  const clearData = () => {
    setKpi(null);
    setDelayStatus(null);
    setShortStatus(null);
    setProblemRows([]);
  };

  // TODO(MIG-T2): [planproblemdashboard/PlanProblemDashboard] 수동 재조회(전역 검색 버튼) 미이관.
  //   old: globalButtons "search" → versionSearchGroupRef.handleSubmit(loadBoard, onErrorInput) —
  //        검색 버튼을 눌러야만 3개 API가 재조회됐다(버전 변경만으로는 재조회 안 됨)
  //   사유: new 대시보드 선례에 전역 버튼 바가 없음(5007/5005/5008과 동일 판정). 대신 버전/plant
  //        변경 시 즉시 자동조회로 연결(Report 트랙 "선택형=자동조회" 규칙) — 조회 기능은 유지된다.
  // 제외(MIG): old DashboardPanel 인프라는 이관 제외 확정(fp-dashboard-screen-guide.md §0-1) —
  //   위젯 레지스트리(TB_UT_WIDGET) / react-grid-layout 드래그·리사이즈 / 위젯 최대화·전체화면.
  //   new는 고정 레이아웃이라 대응 개념이 없다(카드 확대는 §C-4b의 공용 FullScreenButton으로 제공).
  // 제외(MIG): old의 @insight/store 연동 3블록(insightButtonVisible / setInsightData) 미이관 —
  //   Insight는 saas 안에서 AI로 새로 개발 예정이라 **영구 제외 확정**(§0-1). 게다가 old는 두 값의
  //   선언·import가 없어 응답마다 ReferenceError를 던지고 .catch가 삼키던 죽은 코드였다.
  // 제외(MIG): old의 화면 전용 스타일시트 planproblem.css(110줄) 미이관 — old에서도 **import하는
  //   곳이 0건**인 죽은 사본이고(analysis/planproblem 쪽 사본과 byte 동일), 그 선택자
  //   `#UI_FP_PLAN_PROBLEM-problemDetailGrid ...`는 BaseGrid가 DOM id에 난수를 붙여 매칭 자체가
  //   불가능했다. new는 카드/그리드 스타일을 공용 규격(§A-2)으로 잡으므로 대응 파일이 필요 없다.
  // 제외(MIG): old VersionSearchGroup의 location.state 수신 초기화 + AUTO_LOAD 뷰옵션 분기 +
  //   Popover REFRESH(plan-dts 재조회) 아이콘 미이관 — old 인프라(useViewStore) 계열 + new 공용
  //   VersionSearchGroup 소관(이 화면 소관 아님). new는 버전 캐스케이드 확정 시 항상 자동조회한다.
  const loadData = () => {
    const { planScope, versionCd, plantCd } = searchStateRef.current;
    if (!versionCd) return;

    const requestToken = ++latestReqRef.current;
    // 서버가 읽는 필드만 전송(§C-2) — plantCd 배열은 서버 필드명 plantCds로 리네임(old 동일).
    // planningDate는 보내지 않는다: WoPlanQueryRepository가 startTs/endTs를 읽어 기간 필터가 켜진다.
    const params = { planScope, versionCd, plantCds: plantCd };

    gridRef.current?.api?.setGridOption("loading", true);

    fpDashboardService.getProblemKpi(versionCd, params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setKpi(response?.data ?? null);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setKpi(null);
        debug(e);
      });

    fpDashboardService.getProblemStatus(versionCd, params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        // 서버 키: 지연 = deliveryDelayStatus, 계획 실패 = planFailStatus(deliveryShortStatus 아님).
        setDelayStatus(response?.data?.deliveryDelayStatus ?? null);
        setShortStatus(response?.data?.planFailStatus ?? null);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setDelayStatus(null);
        setShortStatus(null);
        debug(e);
      });

    fpDashboardService.getProblemStatusDetail(versionCd, params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setProblemRows(Array.isArray(response?.data) ? response.data : []);
        gridRef.current?.api?.setGridOption("loading", false);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setProblemRows([]);
        gridRef.current?.api?.setGridOption("loading", false);
        debug(e);
      });
  };

  // VersionSearchGroup 캐스케이드(planScope→계획수립일→버전→plant)가 여러 렌더에 걸쳐 순차
  // 커밋되며 각 단계가 조회를 중복 트리거하는 것을 200ms debounce로 합친다(§C-1).
  const debouncedLoadRef = useRef();
  if (!debouncedLoadRef.current) {
    debouncedLoadRef.current = debounce(() => loadData(), 200);
  }
  useEffect(() => () => debouncedLoadRef.current.cancel(), []);

  // 선택형 조회조건 = 즉시 자동조회. plantCd는 캐스케이드가 항상 새 배열로 통지하므로 버전 확정
  // 시점을 놓치지 않는다(같은 조건 재조회는 debounce가 합쳐 무해).
  useEffect(() => {
    if (!versionState.versionCd) {
      debouncedLoadRef.current.cancel();
      // 진행 중이던 조회의 응답이 아래 clearData()를 덮어쓰지 않도록 토큰을 무효화한다(§0-8).
      latestReqRef.current += 1;
      gridRef.current?.api?.setGridOption("loading", false);
      clearData();
      return;
    }
    debouncedLoadRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd, versionState.plantCd]);

  // 문제 현황 상세 — old problemDetailGridItems 15개(표시 10 + 숨김 5) 1:1.
  // old 5008(KpiProblemDetailGrid)과 컬럼 정의가 완전히 동일해(diff 0줄) 같은 정의를 쓴다.
  // TODO(MIG-T2): [planproblemdashboard/PlanProblemDashboard] plantNm 세로 셀 병합 미이관.
  //   old: mergeRule {criteria:"value"} — 같은 공장명 연속 행을 한 셀로 병합
  //   사유: 셀 병합은 Tier2 명시 항목(saas-fp/docs/fp-frontend-migration/CLAUDE.md) — 값 반복 표시로 정보는 동일하다.
  // TODO(MIG-T2): [planproblemdashboard/PlanProblemDashboard] 컬럼 값 목록 필터 5종 미이관.
  //   old: plantNm/woCd/inventoryNm/problemTpCd/problemReasonTpCd에 RealGrid setColumnFilters
  //   사유: 컬럼 필터는 Tier2 명시 항목. old도 필터 목록을 데이터 로드 전에만 생성해 항상 빈
  //        목록이었던 결함이 있어(ProblemDetailGrid.jsx setGridOptions) 실사용 기능이 아니었다.
  const problemColumnDefs = useMemo(() => [
    { headerName: " ", field: "plantCd", hide: true },
    { headerName: "FP_PLANT", field: "plantNm", width: 120 },
    { headerName: "FP_WO_CD", field: "woCd", width: 120 },
    { headerName: " ", field: "inventoryCd", hide: true },
    {
      headerName: "FP_INVENTORY_NM", field: "inventoryNm", width: 160,
      valueGetter: (params) => joinCodeName(params.data?.inventoryCd, params.data?.inventoryNm),
    },
    { headerName: "FP_REQUEST_QTY", field: "requestQty", width: 90, type: ["numberColumn", "rightAligned"] },
    // old dataType:"datetime"은 로케일 dateFormat+timeFormat(초까지)로 표시됐고 백엔드 dueDate도
    // LocalDateTime이라 시각이 실제 데이터다 → 날짜만 보여주면 정보가 잘린다(§C-4c).
    {
      headerName: "FP_DUE_DT", field: "dueDate", width: 150,
      type: ["nonEditableColumn", "centerAligned"], valueFormatter: dateUtils.formatDateTime,
    },
    {
      headerName: "FP_PROBLEM_TP_CD", field: "problemTpCd", width: 120,
      // old valueCallback: transLangKey(problemTpCd) — 코드 값을 그대로 랭키로 번역(키 없으면 원문).
      valueFormatter: (params) => (params.value ? t(params.value) : ""),
    },
    {
      headerName: "FP_PROBLEM_REASON_TP_CD", field: "problemReasonTpCd", width: 140,
      valueFormatter: (params) => (params.value ? t(params.value) : ""),
    },
    {
      headerName: "FP_PROBLEM_ITEM_NM", field: "problemInventoryNm", width: 160,
      valueGetter: (params) => joinCodeName(params.data?.problemInventoryCd, params.data?.problemInventoryNm),
    },
    { headerName: " ", field: "problemInventoryCd", hide: true },
    { headerName: " ", field: "problemRouteCd", hide: true },
    {
      headerName: "FP_PROBLEM_ROUTE_NM", field: "problemRouteNm", width: 140,
      valueGetter: (params) => joinCodeName(params.data?.problemRouteCd, params.data?.problemRouteNm),
    },
    { headerName: " ", field: "problemResourceCd", hide: true },
    {
      headerName: "FP_PROBLEM_RESOURCE_NM", field: "problemResourceNm", width: 150,
      valueGetter: (params) => joinCodeName(params.data?.problemResourceCd, params.data?.problemResourceNm),
    },
  ], [t]);

  // 정적 컬럼이지만 onGridReady 1회가 아니라 effect로 태운다 — 언어 전환(t) 시 헤더 재번역 +
  // 전체화면 전환으로 그리드가 재마운트될 때 새 api에 컬럼 재적용(§C-4b).
  useEffect(() => {
    if (!gridApi) return;
    GridUtils.setColumnDefs({ api: gridApi, columnDefs: problemColumnDefs, viewName, initState: false });
  }, [gridApi, problemColumnDefs, viewName]);

  // 재마운트 직후 1회만 컬럼 상태를 되돌린다. deps를 [gridApi]로 좁힌 것이 핵심 —
  // 위 effect와 합치거나 problemColumnDefs를 deps에 넣으면 언어를 전환할 때 "마지막 전환
  // 시점의 낡은 상태"가 그 사이 사용자가 바꾼 현재 순서를 덮어쓴다. 위 effect가 선언 순서상
  // 먼저 돌아 컬럼 정의가 적용된 뒤에 실행된다(initState:false 경로엔 await가 없어 동기 완료).
  useEffect(() => {
    if (!gridApi || !columnStateRef.current) return;
    gridApi.applyColumnState({ state: columnStateRef.current, applyOrder: true });
  }, [gridApi]);

  // 파이(납기 현황 요약) — old DeliveryStatusSummary.jsx: [okCnt, lateCnt, shortCnt] 3슬라이스,
  // datalabels 없음, 범례 좌측. 5008이 같은 old 컴포넌트를 이관한 것과 동일한 구성이다.
  const deliveryPieData = useMemo(() => {
    if (!kpi) return [];
    return [
      { name: t("FP_ONTIME_DELIVERY"), value: Number(kpi.okCnt ?? 0), color: PIE_COLOR_OK },
      { name: t("FP_DELIVERY_DELAY"), value: Number(kpi.lateCnt ?? 0), color: PIE_COLOR_LATE },
      { name: t("FP_DELIVERY_SHORT"), value: Number(kpi.shortCnt ?? 0), color: PIE_COLOR_SHORT },
    ];
  }, [kpi, t]);
  const pieHasData = deliveryPieData.some((slice) => slice.value > 0);

  // 도넛(지연/실패 사유별 건수) — 변환 규칙은 모듈 상단 buildDonutRows 참고.
  const delayRows = useMemo(() => buildDonutRows(delayStatus, DELAY_COLORS, t), [delayStatus, t]);
  const shortRows = useMemo(() => buildDonutRows(shortStatus, SHORT_COLORS, t), [shortStatus, t]);

  // 카드 헤더 45px + 우측 전체화면 버튼(§A-2/§C-4b). 제목 가운데 정렬을 유지해야 해서 버튼만
  // absolute로 띄운다.
  const cardHeader = (Icon, label, cardId) => (
    <Box sx={{ position: "relative", display: "flex", height: "45px", alignItems: "flex-end", justifyContent: "center" }}>
      <Icon fontSize={"medium"} sx={{ mb: 0.2 }} />
      <Typography className="chart-title" variant="h6" sx={{ pl: 1, textAlign: "center", fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ position: "absolute", right: 0, mr: 0.5 }}>
        {/* setter를 그대로 넘기지 않고 캡처를 얹은 래퍼를 넘긴다 — 공용 FullScreenButton의
            props 계약은 그대로 두고 재마운트 직전 컬럼 상태만 챙긴다. */}
        <FullScreenButton
          fullScreen={fullScreen && fullScreenCardId === cardId}
          setFullScreen={(next) => { captureColumnState(); setFullScreen(next); }}
          screenId={cardId}
          setScreenId={(next) => { captureColumnState(); setFullScreenCardId(next); }}
        />
      </Box>
    </Box>
  );

  const noChartData = (
    <Typography variant="subtitle1" sx={{ p: 3, height: "80%", textAlign: "center", fontWeight: 500 }}>
      {t("noChartData")}
    </Typography>
  );

  // 스탯 타일 — old PlanResultSummary(DetailCard + Typography variant="h4")를 카드 본문 가운데
  // 정렬로 재현. 5008의 진행바 타일(rateTile)과 모양이 달라 공용 컴포넌트로 합치지 않고 화면
  // 로컬로 둔다(§C-3의 "5009 착수 시 재판단" 결론 — 5001 단일 % 타일 착수 때 3종을 놓고 재판단).
  const statTile = (cardId, Icon, label, text, sx) => (
    <Card sx={sx}>
      {cardHeader(Icon, label, cardId)}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "calc(100% - 45px)", px: 2 }}>
        <Typography variant="h4">{text}</Typography>
      </Box>
    </Card>
  );

  // 도넛 + 범례 리스트 — old DeliveryDelayStatus/DeliveryShortStatus의 2/5 : 3/5 세로 분할.
  // 파이/도넛은 ChartComposed가 못 그리는 모양이라 화면에서 recharts를 직접 쓴다(§C-4).
  const donutCard = (cardId, label, rows, sx) => (
    <Card sx={sx}>
      {cardHeader(DonutLargeIcon, label, cardId)}
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "calc(100% - 45px)" }}>
        {rows.length > 0 ? (
          <>
            <Box sx={{ height: "40%", width: "100%" }}>
              <ResponsiveContainer width={"100%"} height={"100%"}>
                <PieChart>
                  <Pie
                    dataKey="value"
                    isAnimationActive={false}
                    data={rows}
                    cx="50%"
                    cy="50%"
                    innerRadius={DONUT_INNER_RADIUS}
                    outerRadius={"85%"}
                  >
                    {rows.map((slice) => (
                      <Cell key={`cell-${slice.code}`} fill={slice.color} stroke={slice.color} />
                    ))}
                  </Pie>
                  <Tooltip isAnimationActive={false} formatter={(value) => formatNumber(value)} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            {/* old는 Chart.js 내장 범례를 끄고(plugins.legend.display:false) 아래 목록으로 대신했다 */}
            <Box sx={{ height: "60%", width: "100%", overflow: "auto" }}>
              <List sx={{ width: "100%" }}>
                {rows.map((slice) => (
                  <Box key={`legend-${slice.code}`}>
                    <ListItem alignItems="flex-start">
                      <ListItemIcon sx={{ minWidth: "inherit", mr: "10px" }}>
                        <SquareIcon sx={{ color: slice.color, fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText
                        sx={{ display: "flex", justifyContent: "space-between" }}
                        primary={slice.name}
                        secondary={formatNumber(slice.value)}
                      />
                    </ListItem>
                    <Divider />
                  </Box>
                ))}
              </List>
            </Box>
          </>
        ) : (
          noChartData
        )}
      </Box>
    </Card>
  );

  // 카드 1개를 그리는 함수 — 일반 배치와 전체화면(카드 하나만 100%) 양쪽에서 같은 정의를 쓴다(§C-4b).
  const renderCard = (cardId, sx) => {
    switch (cardId) {
      case CARD.ORDER_CNT:
        return statTile(
          CARD.ORDER_CNT, AssignmentIcon, t("FP_ORDER_CNT"),
          `${formatNumber(kpi?.totalCnt ?? 0)} ${t("FP_ORDERS")}`, sx
        );

      case CARD.ONTIME_RATE:
        return statTile(
          CARD.ONTIME_RATE, SpeedIcon, t("FP_ONTIME_DELIVERY_RATE"),
          `${formatNumber(kpi?.okRate ?? 0)} %`, sx
        );

      // old 위젯 제목이 "납기 현황 요약 (주문 건)"처럼 랭키 3개 조합이라 그대로 조합한다.
      case CARD.DELIVERY:
        return (
          <Card sx={sx}>
            {cardHeader(PieChartIcon, `${t("FP_DELIVERY_STATUS_SUMMARY")} (${t("FP_ORDER")} ${t("FP_ORDERS")})`, CARD.DELIVERY)}
            <Box width={"100%"} height={"calc(100% - 45px)"}>
              {pieHasData ? (
                <ResponsiveContainer width={"100%"} height={"100%"}>
                  <PieChart>
                    <Pie dataKey="value" isAnimationActive={false} data={deliveryPieData} cx="50%" cy="50%" outerRadius={"80%"}>
                      {deliveryPieData.map((slice) => (
                        <Cell key={`cell-${slice.name}`} fill={slice.color} />
                      ))}
                    </Pie>
                    <Tooltip isAnimationActive={false} formatter={(value) => formatNumber(value)} />
                    <Legend layout="vertical" align="left" verticalAlign="middle" />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                noChartData
              )}
            </Box>
          </Card>
        );

      case CARD.DELAY:
        return donutCard(CARD.DELAY, `${t("FP_DELIVERY_DELAY_STATUS")} (${t("FP_OCCURANCE_CNT")})`, delayRows, sx);

      case CARD.SHORT:
        return donutCard(CARD.SHORT, `${t("FP_DELIVERY_SHORT_STATUS")} (${t("FP_OCCURANCE_CNT")})`, shortRows, sx);

      case CARD.PROBLEM:
        return (
          <Card sx={sx}>
            {cardHeader(TableChartIcon, t("FP_PROBLEM_STATUS_DETAIL"), CARD.PROBLEM)}
            <Box className="ag-theme-balham" width={"100%"} height={"calc(100% - 45px)"}>
              <AgGridReact ref={gridRef} rowData={problemRows} {...defaultGridMemo} onGridReady={(params) => setGridApi(params.api)} />
            </Box>
          </Card>
        );

      default:
        return null;
    }
  };

  // old 레이아웃(12컬럼: 타일 w2 세로 2단 / 파이 w4 / 도넛 w3 / 도넛 w3, 하단 그리드 w12,
  // 상단 h8 : 하단 h6)을 % 비율로 환산 — §B-3 골격.
  return (
    <Box className={"grid-area"} sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 560 }}>
      <FilterContainer hasIcons={false}>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState((prev) => ({ ...prev, ...partial }))}
        />
      </FilterContainer>
      {fullScreen && fullScreenCardId ? (
        // 전체화면 — 검색영역은 그대로 두고 본문만 해당 카드 하나로 교체한다(모달 아님, §C-4b).
        <Box sx={{ display: "flex", width: "100%", flex: 1, minHeight: 0 }}>
          {renderCard(fullScreenCardId, { width: "100%", height: "100%" })}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", flex: 1, minHeight: 0 }}>
          <Box sx={{ display: "flex", width: "100%", height: "calc(57% - 6px)", mb: 1.5 }}>
            <Box sx={{ width: "calc(16.67% - 12px)", height: "100%", mr: 1.5 }}>
              {renderCard(CARD.ORDER_CNT, { width: "100%", height: "calc(50% - 6px)", mb: 1.5 })}
              {renderCard(CARD.ONTIME_RATE, { width: "100%", height: "50%" })}
            </Box>
            {renderCard(CARD.DELIVERY, { width: "calc(33.33% - 12px)", height: "100%", mr: 1.5 })}
            {renderCard(CARD.DELAY, { width: "calc(25% - 12px)", height: "100%", mr: 1.5 })}
            {renderCard(CARD.SHORT, { width: "25%", height: "100%" })}
          </Box>
          {renderCard(CARD.PROBLEM, { width: "100%", height: "43%" })}
        </Box>
      )}
    </Box>
  );
};

export default withTranslation()(PlanProblemDashboard);
